import { portalText, usePortalTranslation, portalLocale } from "../../i18n/portal.js";
/**
 * `data-screen-label="Dashboard"` — drawn to the prototype (FR-APP-3; fidelity wave 2026-09-23).
 *
 * The greeting with the date-and-status kicker; the setup card (progress bar, `n / 5`, the state
 * chips and five numbered steps); the plan card (tier, concurrent-conversation ceiling, included
 * minutes and their bar, "Request quota", "Upgrade plan"); four figure tiles (live conversations
 * with the pulsing dot, minutes today and spend today with their sparklines, answered first time
 * with the transfer count); the agents table; recent calls; "Needs your attention".
 *
 * EVERY NUMBER IS THE GATEWAY'S. The parts of the dashboard the API has no fact for yet — included
 * minutes, the time series behind the sparklines, a workspace-wide conversation list, the
 * attention feed — arrive as `null` from the api gateway (`slice/workspace-home.ts`), and the
 * screen draws the same card with its honest empty line rather than a figure it would have to
 * invent. The sandbox fills them with the prototype's worked example.
 *
 * AMENDED 2026-10-06 (plan-014 E3b; FR12-SCR-07): the plan card names the ORGANIZATION's concurrency ("25 concurrent
 * conversations for the organization") — the entitlement admission enforces, from the usage summary — and says
 * "no minutes included on this plan" only where that is true (`planCeilingLineOf`).
 *
 * AMENDED 2026-09-25 (customer-portal review #45–#48):
 *
 *   - "Needs your attention" no longer waits for an attention feed the API does not have: the
 *     screen derives the items it CAN know from the agent list it already reads — a failed build
 *     (critical, linking to the agent) and a live agent with no number (linking to Numbers & SIP,
 *     where a number is requested). Anything the gateway does send is listed after them.
 *   - The agents table's status chip uses the Agents list's palette: a failed build is the danger
 *     colour and reads "Build failed", the same words and colour as the list.
 *   - The first paint is drawn inside the page frame, so "Adding today up…" is not flush against
 *     the sidebar and the layout does not jump when the figures arrive.
 *   - The "Email confirmed" step says why it may stay open: no confirmation email is sent yet (no
 *     mail sender exists in the platform), so there is nothing to resend and nothing to do.
 *
 * AMENDED 2026-10-09 (design sync, Claude Design version 1791495439483263): no kicker over the greeting and no
 * "New agent" beside "Live calls"; the plan card moved to the foot of the page, titled "<tier> · <rate> a minute",
 * with "Request more lines"; every card takes the console's card finish.
 *
 * AMENDED 2026-10-12 (plan-017 WAPP-A, design §29.11 row 1; owner answers OQ-1, Q1, Q10, PQ-5): no published rate
 * any more. The plan card carries a short table of the organization's own talk rates (`TalkRatePanel`: one line per
 * kind, or one line while all are equal, "Your price" on an own rate), then "Tests are free." and each upcoming change
 * as one line. The title names the rate only while every kind costs the same; "Spent today" says "at your rates" when
 * they differ. Every figure comes from `useTalkRates`.
 *
 * AMENDED 2026-10-28 (WAPP-DASH; owner: "running campaigns or close to start or close from end should have a place in
 * the dashboard"): a Campaigns card under the four figures (`campaign-card.tsx`, where its decided defaults are
 * written) — running and paused campaigns, those ending within a day (as a running row with "Ends …") and those
 * starting within a day, at most five, for a person who may read campaigns; no card at all when none qualifies.
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  Icon,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  Tag,
} from "@wetalk/ui";
import { attentionOf as attentionItemOf } from "@wetalk/view-model/attention";
import { useState, type CSSProperties, type ReactNode } from "react";

import { voiceAgentStatusOf } from "../agents/voice-agent-list-screen.js";
import "./dashboard.css";
import { GLYPH, glyphOf } from "../../shell/glyph.js";
import { partyLabel } from "../../api/conversation-view.js";
import { useGateway } from "../../api/provider.js";
import { LIVE_ACTIVITY_REFETCH_MS, QUERY_KEY, agentTemplateQuery, dashboardQuery, voiceAgentListQuery } from "../../api/query.js";
import { ResendVerificationButton } from "../mail-landing/resend-verification.js";
import type { DashboardFiguresView, VoiceAgentState, VoiceAgentView } from "../../api/gateway.js";
import type { DashboardTrendView } from "../../api/slice/workspace-home.js";
import { formatMoney } from "../../money.js";
import { oneTalkRateOf, talkRateText, useTalkRates } from "../../api/slice/talk-rate.js";
import { TalkRatePanel } from "../billing/talk-rate-panel.js";
import { QuotaRequestDialog } from "../billing/quota-request-dialog.js";
import { talkTimeText } from "../../talk-time.js";
import type { VoiceAgentTabId } from "../agents/[id]/agent-tabs.js";
import type { EntitlementOverviewView } from "../../api/slice/entitlement.js";
import { noMinutesIncludedTrue } from "../finance/allowances/allowance-copy.js";
import { useOrganizationAllowance } from "../finance/allowances/organization-allowance.js";
import { CampaignCard } from "./campaign-card.js";

/** The prototype's "Recent calls" shows the latest four. */
const RECENT_ROW_COUNT = 4;

/** The agent search the dashboard's links open: the Live tab, today. */
const LIVE_TAB = { tab: "live", range: "7d", outcome: "all", q: "", conversation: "" } as const;

/**
 * The greeting by the member's own clock (2026-10-09: it said "Good evening" at every hour). Morning until
 * noon, afternoon until 18:00, evening after — the prototype's three words.
 */
export function greetingOf(hour: number): string {
  if (hour < 12) return portalText("core", "dashboard.good_morning_0f7689");
  if (hour < 18) return portalText("core", "dashboard.good_afternoon_13fd0a");
  return portalText("core", "dashboard.good_evening_6a4f54");
}

const STATE_LABEL: Readonly<Record<VoiceAgentState, string>> = {
  get draft() { return portalText("core", "dashboard.draft_23d33e"); },
  get building() { return portalText("core", "dashboard.building_9d914b"); },
  // glossary-lint-disable-next-line live -- the agent_state enum value, voice_agent.state='live'
  get live() { return portalText("core", "dashboard.live_65c821"); },
  get paused() { return portalText("core", "dashboard.paused_c7dfb6"); },
  get scheduled() { return portalText("core", "dashboard.scheduled_1cd1bd"); },
  get failed() { return portalText("core", "dashboard.build_failed_2f8e46"); },
};

/**
 * The Agents list's palette (`voice-agent-list-screen.tsx` `StateTag`), so one state reads the
 * same colour on every screen (review #46): a failed build is the danger accent, Live and Building
 * are the accent, everything else is neutral.
 */
export function stateToneOf(state: VoiceAgentState): "accent" | "accent-2" | "neutral" {
  if (state === "failed") return "accent-2";
  return state === "live" || state === "building" ? "accent" : "neutral";
}

/** One "Needs your attention" line, with where to act. */
export interface AttentionLine {
  readonly key: string;
  readonly tone: "critical" | "muted" | "positive";
  readonly text: string;
  readonly action:
    | { readonly kind: "voice_agent"; readonly label: string; readonly voiceAgentId: string; readonly tab: VoiceAgentTabId }
    | { readonly kind: "numbers"; readonly label: string }
    | null;
}

/**
 * What the dashboard can know needs the owner, from the agent list alone (review #45): a failed
 * build cannot answer anything, and a live agent with no number cannot be reached by a caller.
 * Anything the gateway's own attention feed carries follows these.
 *
 * Which items, in what order, is `@wetalk/view-model/attention`'s (plan-034 VIEW-PKG), shared with
 * the wetalk app; the words and where each action leads are the portal's, here.
 */
export function attentionOf(
  voiceAgent: readonly VoiceAgentView[],
  fed: DashboardFiguresView["attention"],
): readonly AttentionLine[] {
  return attentionItemOf(voiceAgent, fed).map((item): AttentionLine => {
    switch (item.code) {
      case "build_failed":
        return {
          key: item.key,
          tone: item.tone,
          get text() { return portalText("core", "dashboard._the_build_failed_so_this_agent_cannot_answer_74dea9", { value0: item.displayName }); },
          action: { kind: "voice_agent", label: portalText("core", "dashboard.see_what_went_wrong_76babd"), voiceAgentId: item.voiceAgentId, tab: "versions" },
        };
      case "no_party_number":
        return {
          key: item.key,
          tone: item.tone,
          get text() { return portalText("core", "dashboard._is_live_but_has_no_phone_number_so_no_caller_can_reach_it_607350", { value0: item.displayName }); },
          action: { kind: "numbers", label: portalText("core", "dashboard.request_a_number_705e33") },
        };
      case "fed":
        return {
          key: item.key,
          tone: item.tone,
          text: item.fed.text,
          action:
            item.fed.action === null
              ? null
              : { kind: "voice_agent", label: item.fed.action.label, voiceAgentId: item.fed.action.voiceAgentId, tab: "review" },
        };
    }
  });
}



/** A bar's fill, as the one custom property its rule reads (the house pattern for meters). */
function fillStyle(percent: number): CSSProperties {
  return { "--c-meter-fill": `${String(Math.max(0, Math.min(100, percent)))}%` } as CSSProperties;
}

/** `142` → "2:22". */
function durationText(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  return `${String(minutes)}:${String(seconds % 60).padStart(2, "0")}`;
}

/** 25 000 / 3 118 → "3,118". */
function countText(value: number): string {
  return new Intl.NumberFormat(portalLocale() === "en" ? "en-GB" : "el-GR").format(value);
}

/** The prototype's 80 × 28 sparkline, the series scaled to its own maximum. */
function Sparkline({ trend }: { readonly trend: DashboardTrendView }): ReactNode {
  usePortalTranslation("core");
  const top = Math.max(...trend.point, 1);
  const last = Math.max(trend.point.length - 1, 1);
  const points = trend.point
    .map((value, index) => `${String(Math.round((index / last) * 80))},${String(28 - Math.round((value / top) * 25))}`)
    .join(" ");
  return (
    <svg className="c-dash-spark" viewBox="0 0 80 28" aria-hidden="true">
      <polyline points={points} />
    </svg>
  );
}

function SetupCard({ setup }: { readonly setup: NonNullable<DashboardFiguresView["setup"]> }): ReactNode {
  usePortalTranslation("core");
  const doneCount = setup.step.filter((one) => one.done).length;
  const percent = Math.round((doneCount / Math.max(setup.step.length, 1)) * 100);
  return (
    <div className="c-dash-setup">
      <div className="c-dash-setup-track">
        <div className="c-dash-setup-fill" style={fillStyle(percent)} />
      </div>
      <div className="c-dash-setup-head">
        <span className="c-dash-setup-title">{portalText("core", "dashboard.finish_setting_up_your_workspace_7e3674")}</span>
        <span className="c-dash-setup-count">
          {String(doneCount)} / {String(setup.step.length)}
        </span>
        <span className="c-dash-setup-chips">
          {setup.chip.map((one) => (
            <Tag key={one.label} tone={one.positive ? "accent" : "neutral"}>
              {one.label}
            </Tag>
          ))}
        </span>
      </div>
      <ol className="c-dash-setup-steps">
        {setup.step.map((one, index) => (
          <li key={one.label} className={one.done ? "c-dash-step c-dash-step-done" : "c-dash-step"}>
            <span className="c-dash-step-dot">
              {one.done ? <Icon glyph={GLYPH.check} size="xs" /> : String(index + 1)}
            </span>
            <span className="c-dash-step-label">
              {one.label}
              {/* AMENDED 2026-10-11 (owner answer 66, mail on): an open "Email confirmed" offers the link again, as first-run does. */}
              {!one.done && one.label === portalText("core", "dashboard.email_confirmed_86e753") ? (
                <span className="c-dash-step-note">
                  <ResendVerificationButton refresh={[QUERY_KEY.dashboard, QUERY_KEY.authSession, QUERY_KEY.firstRun]} />
                </span>
              ) : null}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}

/**
 * The plan card's ceiling line (plan-014 E3b; FR12-ENT-09, FR12-SCR-07): the organization's concurrency — the
 * entitlement admission enforces, which the usage summary carries for every member (INT 2026-10-07) — and "no minutes
 * included on this plan" only where it is true (allowances read, no talk time granted now).
 */
export function planCeilingLineOf(figures: DashboardFiguresView, allowance: EntitlementOverviewView | null): string {
  const allowed = figures.organizationConcurrency;
  const usage = figures.planUsage;
  const included =
    usage !== null
      ? portalText("core", "dashboard._minutes_included_08699d", { value0: usage.includedMinuteText })
      : allowance !== null && noMinutesIncludedTrue(allowance.grant, new Date(allowance.at))
        ? portalText("core", "dashboard._no_minutes_included_on_this_plan_6e57e7")
        : "";
  return portalText("core", "dashboard._concurrent_conversations_for_the_organization_15a0be", { value0: String(allowed), value1: included });
}

function PlanCard({ figures }: { readonly figures: DashboardFiguresView }): ReactNode {
  usePortalTranslation("core");
  const allowance = useOrganizationAllowance();
  const rates = useTalkRates();
  // Q1: the title names one rate only while every kind costs the same; the table below lists them either way.
  const oneRate = rates.data === undefined ? null : oneTalkRateOf(rates.data.talkRates);
  const usage = figures.planUsage;
  const [quotaRequestOpen, setQuotaRequestOpen] = useState(false);
  const usedPercent =
    usage === null ? 0 : Math.floor((usage.usedMinute / Math.max(usage.includedMinute, 1)) * 100);
  return (
    <div className="c-dash-plan">
      <span className="c-dash-plan-mark">
        <Icon glyph={GLYPH.lightning} size="2xl" />
      </span>
      <span className="c-dash-plan-body">
        <span className="c-dash-plan-name">
          <span className="c-dash-plan-title">
            {figures.tier.name}
            {oneRate === null ? null : ` · ${talkRateText(oneRate.perMinute, "minute")}`}
          </span>
          <span className="c-dash-plan-current">
            <Tag tone="accent">{portalText("core", "dashboard.current_4fc0e2")}</Tag>
          </span>
        </span>
        <span className="c-dash-plan-dek">{planCeilingLineOf(figures, allowance)}</span>
        {usage === null ? (
          <>
            {/* The prototype's bar and "18 of 25 channels in use at today's peak · September statement closes …": the
                figures carry the lines in use now, not today's peak or the statement's close, so the bar is now's. */}
            <span className="c-dash-plan-track">
              <span
                className="c-dash-plan-fill"
                style={fillStyle(Math.floor((figures.channelUnitInUse / Math.max(figures.organizationConcurrency, 1)) * 100))}
              />
            </span>
            <span className="c-dash-plan-meta">
              {portalText("core", "dashboard.channels_in_use_of", {
                value0: String(figures.channelUnitInUse),
                value1: String(figures.organizationConcurrency),
              })}
            </span>
          </>
        ) : (
          <>
            <span className="c-dash-plan-track">
              <span className="c-dash-plan-fill" style={fillStyle(usedPercent)} />
            </span>
            <span className="c-dash-plan-meta">
              {countText(usage.usedMinute)}{" "}{portalText("core", "dashboard.of_de04fa")}{" "}{usage.includedMinuteText}{" "}{portalText("core", "dashboard.minutes_747491")}{" "}
              {String(usedPercent)}{portalText("core", "dashboard._used_next_cycle_099946")}{" "}{usage.nextCycleText}
            </span>
          </>
        )}
        <TalkRatePanel variant="dashboard" />
      </span>
      <span className="c-dash-plan-actions">
        {/* As on an agent's Live tab: the concurrency request opens in place (the prototype's `requestQuota`). */}
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => {
            setQuotaRequestOpen(true);
          }}
        >
          {portalText("core", "dashboard.request_more_lines_cba205")}
        </button>
        {/* plan-020 WAPP-BILL (ED-22, design §32.17): "Upgrade plan" opens the plans, where a plan is chosen and bought. */}
        <Link to="/finance/plans" className="btn btn-primary">
          <Icon glyph={GLYPH.lightning} size="md" />{portalText("core", "dashboard.upgrade_plan_e80fe7")}</Link>
      </span>
      <QuotaRequestDialog
        open={quotaRequestOpen}
        currentCeiling={figures.organizationConcurrency}
        onClose={() => {
          setQuotaRequestOpen(false);
        }}
      />
    </div>
  );
}

function Figures({ figures }: { readonly figures: DashboardFiguresView }): ReactNode {
  usePortalTranslation("core");
  const answered = figures.answeredFirstTimePercent;
  const rates = useTalkRates();
  const oneRate = rates.data === undefined ? null : oneTalkRateOf(rates.data.talkRates);
  return (
    <div className="c-dash-figures">
      <div className="c-dash-figure">
        <div className="c-dash-figure-label">
          <span className="c-dash-pulse" />{portalText("core", "dashboard.live_conversations_cca62c")}</div>
        <div className="c-dash-figure-value">{String(figures.liveConversationCount)}</div>
        <div className="c-dash-figure-note">{portalText("core", "dashboard.inbound_outbound_a6785c")}</div>
        <div className="c-dash-figure-rule" />
      </div>
      <div className="c-dash-figure">
        <div className="c-dash-figure-label">{portalText("core", "dashboard.minutes_today_ace9e3")}</div>
        <div className="c-dash-figure-row">
          <span className="c-dash-figure-value">{talkTimeText(figures.talkSecondToday)}</span>
          {figures.minutesTrend === null ? null : <Sparkline trend={figures.minutesTrend} />}
        </div>
        <div className="c-dash-figure-note c-dash-figure-note-positive">
          {figures.minutesTrend?.deltaText ?? portalText("core", "dashboard.since_midnight_da9225")}
        </div>
        <div className="c-dash-figure-rule" />
      </div>
      <div className="c-dash-figure">
        <div className="c-dash-figure-label">{portalText("core", "dashboard.spent_today_6a6d69")}</div>
        <div className="c-dash-figure-row">
          <span className="c-dash-figure-value">{formatMoney(figures.spendToday)}</span>
          {figures.spendTrend === null ? null : <Sparkline trend={figures.spendTrend} />}
        </div>
        <div className="c-dash-figure-note">
          {oneRate === null
            ? portalText("core", "talk_rate.at_your_rates_on_call_only")
            : portalText("core", "talk_rate.on_call_only", { value0: talkRateText(oneRate.perMinute, "minute") })}
        </div>
        <div className="c-dash-figure-rule" />
      </div>
      <div className="c-dash-figure">
        {/* `null` is a day with no inbound conversation (C21): a share of nothing is not 0%. */}
        <div className="c-dash-figure-label">
          {answered === null ? portalText("core", "dashboard.answered_first_time_no_calls_yet_today_06917b") : portalText("core", "dashboard.answered_first_time_7cefc5")}
        </div>
        <div className="c-dash-figure-value">{answered === null ? "—" : `${String(answered)}%`}</div>
        <div className="c-dash-figure-note">
          {figures.transferCountToday === null
            ? portalText("core", "dashboard.of_inbound_conversations_today_5913da")
            : portalText("core", "dashboard.transfers_to_staff", { count: figures.transferCountToday })}
        </div>
        <div className="c-dash-figure-rule" />
      </div>
    </div>
  );
}

export function DashboardScreen(): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  // Refetched on every visit, fresh or not: the figures are today's money and live conversations,
  // and an agent built a moment ago (the wizard hands back here) must be on the table at once —
  // a cache still inside its stale time used to show "Nothing is answering yet" beside it.
  // AMENDED 2026-10-09 (owner: "live conversations and minutes today do not update"): read again every five
  // seconds while the page is open, like Live activity (`LIVE_ACTIVITY_REFETCH_MS`); React Query pauses it in a
  // background tab. One small read beats a stream per agent (the board stream is per agent).
  const figures = useQuery({ ...dashboardQuery(gateway), refetchOnMount: "always", refetchInterval: LIVE_ACTIVITY_REFETCH_MS });
  const voiceAgentList = useQuery({ ...voiceAgentListQuery(gateway), refetchOnMount: "always", refetchInterval: LIVE_ACTIVITY_REFETCH_MS });
  const agentTemplate = useQuery(agentTemplateQuery(gateway));

  if (figures.data === undefined) {
    // Inside the page frame (review #47): the note sits where the greeting will, so nothing jumps.
    return (
      <section className="c-page c-dash">
        <p className="c-note">{portalText("core", "dashboard.adding_today_up_b49f71")}</p>
      </section>
    );
  }
  const attention = attentionOf(voiceAgentList.data ?? [], figures.data.attention);

  const one = figures.data;
  const iconOf = (agentTemplateCode: string): ReactNode => {

    const card = agentTemplate.data?.find((row) => row.code === agentTemplateCode);
    return <Icon glyph={card === undefined ? GLYPH.headset : glyphOf(card.iconName)} size="lg" />;
  };

  return (
    <section className="c-page c-dash">
      <div className="c-headline-row c-dash-head">
        <div>
          <h1>
            {greetingOf(new Date().getHours())}, {one.greetingName}
          </h1>
        </div>
        <div className="c-dash-head-actions">
          {/* Live activity, every conversation in flight on every agent (owner, 2026-10-09). The prototype's
              `go.agent` opens its one demo agent; here that landed on whichever agent was first in the list. */}
          <Link to="/live" className="btn btn-secondary">
            <Icon glyph={GLYPH.broadcast} size="md" />{portalText("core", "dashboard.live_calls_d460b3")}</Link>
        </div>
      </div>

      {one.setup === null ? null : <SetupCard setup={one.setup} />}
      <Figures figures={one} />
      {/* WAPP-DASH (2026-10-28): campaigns calling now, or starting or ending within a day — or nothing. */}
      <CampaignCard voiceAgent={voiceAgentList.data ?? []} />

      <div className="c-dash-section-head">
        <p className="c-dash-label">{portalText("core", "dashboard.agents_64acf7")}</p>
        <Link to="/agents" search={{ status: "all", q: "" }} className="c-dash-more">{portalText("core", "dashboard.all_agents_b94d6c")}</Link>
      </div>
      {voiceAgentList.data === undefined || voiceAgentList.data.length === 0 ? (
        <p className="c-note">{portalText("core", "dashboard.nothing_is_answering_yet_c14212")}{" "}<Link to="/agents/new">{portalText("core", "dashboard.create_your_first_agent_e9f219")}</Link>.
        </p>
      ) : (
        <div className="c-dash-table">
          <Table caption={portalText("core", "dashboard.your_agents_with_today_s_minutes_and_spend_feee62")} captionVisible={false}>
            <TableHead>
              <TableRow>
                <TableHeaderCell>{portalText("core", "dashboard.agent_5ce2e6")}</TableHeaderCell>
                <TableHeaderCell>{portalText("core", "dashboard.template_3ec1ae")}</TableHeaderCell>
                <TableHeaderCell>{portalText("core", "dashboard.status_bae7d5")}</TableHeaderCell>
                <TableHeaderCell>{portalText("core", "dashboard.number_b7baa1")}</TableHeaderCell>
                <TableHeaderCell numeric>{portalText("core", "dashboard.minutes_today_ace9e3")}</TableHeaderCell>
                <TableHeaderCell numeric>{portalText("core", "dashboard.cost_64ae43")}</TableHeaderCell>
                <TableHeaderCell>
                  <span className="c-visually-hidden">{portalText("core", "dashboard.open_cf9b77")}</span>
                </TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {voiceAgentList.data.map((row) => (
                <TableRow key={row.voiceAgentId}>
                  <TableCell>
                    <Link
                      to="/agents/$voiceAgentId"
                      params={{ voiceAgentId: row.voiceAgentId }}
                      search={LIVE_TAB}
                      className="c-dash-agent"
                    >
                      <span className="c-dash-agent-mark">{iconOf(row.agentTemplateCode)}</span>
                      <span className="c-dash-agent-name">{row.displayName}</span>
                    </Link>
                  </TableCell>
                  <TableCell>{row.agentTemplateName}</TableCell>
                  <TableCell>
                    {voiceAgentStatusOf(row).label === portalText("core", "dashboard.calling_91f63f") ? (
                      <Tag tone="accent">{portalText("core", "dashboard.calling_91f63f")}</Tag>
                    ) : (
                      <Tag tone={stateToneOf(row.state)}>{STATE_LABEL[row.state]}</Tag>
                    )}
                  </TableCell>
                  <TableCell>{row.partyNumber ?? portalText("core", "dashboard.not_connected_yet_d98c2e")}</TableCell>
                  <TableCell numeric>{talkTimeText(row.talkSecondToday)}</TableCell>
                  <TableCell numeric>{formatMoney(row.spendToday)}</TableCell>
                  <TableCell>
                    <span className="c-dash-caret">
                      <Icon glyph={GLYPH.caretRight} size="md" />
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <div className="c-dash-lower">
        <div>
          <div className="c-dash-section-head c-dash-section-head-lower">
            <p className="c-dash-label">{portalText("core", "dashboard.recent_calls_f584cb")}</p>
            {voiceAgentList.data?.[0] === undefined ? null : (
              <Link
                to="/agents/$voiceAgentId"
                params={{ voiceAgentId: voiceAgentList.data[0].voiceAgentId }}
                search={{ ...LIVE_TAB, tab: "history" }}
                className="c-dash-more"
              >{portalText("core", "dashboard.full_history_bde98b")}</Link>
            )}
          </div>
          {one.recentConversation === null ? (
            <p className="c-note">{portalText("core", "dashboard.recent_calls_across_your_agents_are_listed_on_each_agent_s_histor_6927d0")}</p>
          ) : one.recentConversation.length === 0 ? (
            <p className="c-note">{portalText("core", "dashboard.no_calls_yet_today_406748")}</p>
          ) : (
            <div className="c-dash-table c-dash-recent">
              <Table caption={portalText("core", "dashboard.the_latest_conversations_2e9b10")} captionVisible={false}>
                <TableBody>
                  {one.recentConversation.slice(0, RECENT_ROW_COUNT).map((row) => (
                    <TableRow key={row.conversationId}>
                      <TableCell numeric>
                        <span className="c-dash-recent-time">{row.timeText}</span>
                      </TableCell>
                      {/* A website visitor is never printed by key (plan-004 WC-90). */}
                      <TableCell>{row.partyAddress === null ? portalText("core", "dashboard.withheld_96ac7e") : partyLabel(row.partyAddress)}</TableCell>
                      <TableCell>{row.outcome ?? "—"}</TableCell>
                      <TableCell numeric>{durationText(row.billedSeconds)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
        <div>
          <p className="c-dash-label c-dash-label-lower">{portalText("core", "dashboard.needs_your_attention_e51f42")}</p>
          {attention.length === 0 ? (
            <p className="c-note">{portalText("core", "dashboard.nothing_needs_you_right_now_fe3735")}</p>
          ) : (
            <ul className="c-dash-attention">
              {attention.map((item) => (
                <li key={item.key} className={`c-dash-attention-item c-dash-attention-${item.tone}`}>
                  <span className="c-dash-attention-glyph">
                    <Icon
                      glyph={
                        item.tone === "critical"
                          ? GLYPH.warningCircle
                          : item.tone === "positive"
                            ? GLYPH.checkCircle
                            : GLYPH.phoneX
                      }
                      size="xl"
                    />
                  </span>
                  <span className="c-dash-attention-text">
                    {item.text}{" "}
                    {item.action === null ? null : item.action.kind === "numbers" ? (
                      <Link to="/numbers">{item.action.label}</Link>
                    ) : (
                      <Link
                        to="/agents/$voiceAgentId"
                        params={{ voiceAgentId: item.action.voiceAgentId }}
                        search={{ ...LIVE_TAB, tab: item.action.tab }}
                      >
                        {item.action.label}
                      </Link>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Design sync 2026-10-09: the plan card closes the page (it opened it before). */}
      <PlanCard figures={one} />
    </section>
  );
}
